import { ofNullable, map, filter, orElse } from 'module/ui/core/maybe';
import { duration } from 'moment';
import { pipe } from 'ramda';
import { overrides } from 'translations/en-moment';

export const format = (key: keyof typeof overrides, value: number): string =>
  overrides[key].replace(
    '%d',
    value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
  );

const overridesValue = {
  s: 'Just now',
  ss: '%ds',
  m: '1m',
  mm: '%dm',
  h: '1h',
  hh: '%dh',
  d: '1d',
  dd: '%dd',
  M: '1mo',
  MM: '%dmo',
  y: '1y',
  yy: '%dy',
};

const formatWithSeconds = (
  key: keyof typeof overridesValue,
  value: number,
): string =>
  overridesValue[key].replace(
    '%d',
    value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
  );

// Adding this to always display minutes as well
export const formatterWithMinutes = pipe(
  pipe(
    (minutes: number | string | undefined) => minutes?.toString() ?? null,
    ofNullable,
    map((minutes: string) => parseInt(minutes, 10)),
    filter((minutes: number) => !Number.isNaN(minutes)),
    map((minutes: number) => Math.min(Math.max(minutes, 0))),
    orElse(0),
    (minutes) => duration(minutes, 'minutes'),
    (m) => [duration(Math.floor(m.asHours()), 'hours'), m],
  ),
  ([hours, minutes]) => [hours.asHours(), minutes.subtract(hours).asMinutes()],
  ([h, m]) => [format('hh', h), format('mm', m).padStart(h > 0 ? 3 : 2, '0')],
  (values) => values.join(' ').replace(/^0h /, '').replace(/^00m$/, '0m'),
);

export const formatter = pipe(formatterWithMinutes, (value) =>
  value.replace(/^([\d,]{3,}h) ([\d]{2}m)$/g, '$1'),
);

export const formatterWithMinutesAndSeconds = pipe(
  pipe(
    (minutes: number | string | undefined) => minutes?.toString() ?? null,
    ofNullable,
    map((minutes: string) => Number(minutes)),
    filter((minutes: number) => !Number.isNaN(minutes)),
    map((minutes: number) => Math.min(Math.max(minutes, 0))),
    orElse(0),
    (minutes: number) => duration(minutes, 'minutes'),
    (m) => [
      Math.floor(m.asHours()),
      m.minutes(),
      Math.round(m.seconds() + m.milliseconds() / 1000),
    ],
  ),
  ([h, m, s]) => {
    let hourFormatter = formatWithSeconds('hh', h);
    let minFormatter = formatWithSeconds('mm', m).padStart(h > 0 ? 3 : 2, '0');
    const secFormatter = formatWithSeconds('ss', s).padStart(
      m > 0 ? 3 : 2,
      '0',
    );

    if (h > 0 && s > 30) {
      if (m === 59) {
        s = 0;
        m = 0;
        h += 1;
        minFormatter = formatWithSeconds('mm', m).padStart(h > 0 ? 3 : 2, '0');
        hourFormatter = formatWithSeconds('hh', h);
      } else {
        s = 0;
        m += 1;
        minFormatter = formatWithSeconds('mm', m).padStart(h > 0 ? 3 : 2, '0');
      }
    }

    if (h > 0) {
      return [hourFormatter, minFormatter]
        .join(' ')
        .replace(/^0h /, '')
        .replace(/^00m$/, '0m');
    }

    if (s === 60) {
      s = 0;
      m += 1;
      minFormatter = formatWithSeconds('mm', m).padStart(h > 0 ? 3 : 2, '0');
    }

    if (s > 0) {
      return [minFormatter, secFormatter].join(' ').replace(/^0m /, '');
    }

    return minFormatter.toString().replace(/^00m$/, '0m');
  },
);
